/**
 * INV-04 · As definições canônicas de "este usuário ainda não terminou de
 * configurar o negócio".
 *
 * ── Por que são DOIS predicados, e não um ────────────────────────────────────
 *
 * Porque são dois efeitos diferentes, e tratá-los como um só quebra metade da
 * base. Existem dois jeitos de chegar "incompleto" na área logada:
 *
 * 1. **Login social ("Continuar com Google")** — o backend cria o `User` e
 *    NENHUM `Tenant`. Não há `tenantId`, e todas as telas de produto (agenda,
 *    serviços, clientes, conversas) são tenant-scoped: literalmente não existe
 *    o que renderizar. Até 21/08/2026 esse usuário caía em `/visao-geral` e via
 *    uma tela vazia, sem erro e sem saída. Aqui **bloquear é honesto** — é o
 *    `mustCompleteOnboarding`.
 *
 * 2. **Signup por e-mail/senha** — `authController.ts:168-177` cria o tenant com
 *    `name: "Minha Empresa"` e **sem `segment`** (a coluna é nullable, ver
 *    `schema.prisma:101`). Esse usuário tem tenant, tem agenda, tem serviços e
 *    tem um produto funcionando. Falta-lhe o segmento, que só afeta a copy, o
 *    filtro do menu lateral e o tom do bot. Bloquear aqui trancaria a base
 *    inteira de e-mail/senha — inclusive os dois tenants de produção — para
 *    fora da própria agenda. É o `needsOnboarding`, que **sinaliza** (banner) e
 *    não bloqueia.
 *
 * Um predicado só forçaria escolher entre não dar saída ao usuário do Google
 * (caso 1) ou trancar quem já usa o produto (caso 2). São dois, então.
 *
 * ── Por que o critério do caso 2 é o `segment`, e não o nome do negócio ───────
 *
 * Havia TRÊS critérios divergentes no `src/`:
 *
 *   • `Agenda.js` .... `!response.data.tenant?.segment`      ← o correto
 *   • `Dashboard.js` . `freshUser.businessName === "Minha Empresa"`   ← morto
 *   • `Dashboard.js` . `!tenantId || businessName === "Minha Empresa"` ← morto
 *
 * Os dois do `Dashboard` eram metade-mortos por um typo do backend: o
 * `authController.ts` serializa a chave como `businesName` (com um "s" só), de
 * modo que `user.businessName` era SEMPRE `undefined` e a comparação SEMPRE
 * falsa. O critério nunca disparou — e ninguém percebeu, porque critério que
 * nunca dispara não produz erro, produz ausência.
 *
 * Mesmo com o typo corrigido, o nome seria o critério errado: "Minha Empresa" é
 * um placeholder que o backend pode deixar de usar sem avisar, e um negócio que
 * legitimamente se chame assim ficaria marcado como incompleto para sempre.
 * `segment` é diferente — é `enum BusinessSegment`, sem default, e só é
 * preenchido por quem passou pelo wizard. É o único campo cuja presença PROVA
 * que o onboarding aconteceu.
 *
 * ── Por que ambos são fail-safe (usuário ausente ⇒ `false`) ───────────────────
 *
 * O contexto de auth começa com `user === null` e só hidrata depois do
 * `/auth/me`. Se a ausência de usuário significasse "incompleto", toda rota
 * autenticada redirecionaria para `/onboarding` no primeiro render — inclusive
 * para quem tem tenant há meses. Quem sabe distinguir "ainda não hidratou" de
 * "não tem tenant" é a guarda, via `loading` do `AuthContext`. Estas funções
 * respondem apenas sobre um usuário que já se conhece; sem usuário, se calam.
 *
 * Os três aceitam tanto o perfil de `/auth/me` (`AuthContext.user`) quanto o
 * envelope de `GET /tenants/me` — os dois expõem o tenant em `.tenant`. É de
 * propósito: obrigar cada chamador a normalizar o formato antes de perguntar
 * seria reabrir a porta para critérios divergentes.
 */

/**
 * O segmento do tenant, ou `null`. Acessor único do caminho `.tenant.segment` —
 * os dois predicados abaixo e a pré-seleção do wizard derivam daqui, para que o
 * caminho exista escrito em um lugar só.
 *
 * @param {object|null|undefined} user
 * @returns {string|null}
 */
export function getTenantSegment(user) {
  return user?.tenant?.segment ?? null;
}

/**
 * O nome do negócio, ou `null`. Acessor único do caminho `.tenant.name`, pelo
 * mesmo motivo do `getTenantSegment` acima: o wizard precisa PRÉ-PREENCHER o
 * campo em modo edição, e um `user?.tenant?.name` digitado lá dentro seria mais
 * uma leitura solta do mesmo dado.
 *
 * ⚠️ NÃO confundir com `user.businessName`, que o backend nunca envia — a chave
 * sai serializada com typo (`businesName`) do `authController.ts:426`. O nome
 * confiável mora em `.tenant.name`.
 *
 * @param {object|null|undefined} user
 * @returns {string|null}
 */
export function getTenantName(user) {
  return user?.tenant?.name ?? null;
}

/**
 * **BLOQUEIA navegação.** `true` só quando o usuário é conhecido e não tem
 * negócio nenhum — o caso do login social. Consumido pelo `ProtectedRoute` e
 * pelo `AuthSuccess`.
 *
 * ⚠️ Checa AS DUAS formas de objeto-usuário que circulam no app, e é obrigatório
 * que cheque as duas:
 *
 *   • o perfil de `/auth/me` .......... `{ ..., tenant: { id, segment, … } }`
 *   • o gravado em `localStorage` ..... `{ id, email, tenantId }`, SEM `.tenant`
 *     (escrito por `AuthSuccess` e por `authService.login`)
 *
 * Olhar só `.tenant` faz o segundo formato parecer "sem negócio". Isso é inócuo
 * enquanto o `/auth/me` responde — mas quando ele cai com 5xx ou erro de rede
 * (não 401, que o interceptor trata), o `AuthContext` degrada para o perfil de
 * storage, e um dono com tenant há meses seria mandado para `/onboarding`. Lá o
 * wizard fixaria `isCreating = true`, ele preencheria tudo, o `/tenants/setup`
 * encontraria o tenant existente e devolveria 200 DESCARTANDO o formulário — e
 * o modal anunciaria "Dados salvos com sucesso!" sobre um no-op, com a guarda
 * devolvendo o usuário para `/onboarding` em seguida. Sucesso declarado sobre
 * nada, num laço.
 *
 * Não afrouxa o caso do Google: lá o `tenantId` vem `null` no fragmento e é
 * gravado como `null`.
 *
 * @param {object|null|undefined} user
 * @returns {boolean}
 */
export function mustCompleteOnboarding(user) {
  if (!user) return false;
  return !user.tenant && !user.tenantId;
}

/**
 * **NÃO bloqueia.** `true` quando o negócio está incompleto: sem tenant, ou com
 * tenant sem `segment`. Consumido pelo banner do `AppLayout` e pela decisão de
 * abrir o wizard.
 *
 * Superconjunto de `mustCompleteOnboarding`: quem precisa completar também está
 * incompleto. A recíproca é falsa, e é justamente aí que mora a base de
 * e-mail/senha.
 *
 * @param {object|null|undefined} user
 * @returns {boolean}
 */
export function needsOnboarding(user) {
  if (!user) return false;
  return !getTenantSegment(user);
}
